import { Button } from "@mui/material"
import { observer } from "mobx-react-lite"
import MergeConflict from "./MergeConflict"
import { Theme } from "../../../../misc/Theme"
import { store } from "../../../../stores/Store"
import InlineMenu from "../../../../misc/InlineMenu"
import { CheckIcon } from "../../../views/questionnaire/QuestionnaireTable"
import { formatFieldKey, formatValue, keysToIgnore, updatableFieldNames } from "./mergeUtils"


interface MergeFieldProps {
    dataSources: any[];
    fieldKey: string;
    showConflict?: boolean;
}


const MergeField = observer(({dataSources, fieldKey, showConflict = false}: MergeFieldProps) => {

    const dataSourcesWithOverrides = [store.QuestionnaireStore.mergeOverride, ...dataSources]
    const dataSource = dataSourcesWithOverrides.find(ds => fieldKey in ds)
    const valueToUse = dataSource[fieldKey]
    const iconId = dataSource["iconId"]

    const releventDataSources = dataSources.filter(ds => fieldKey in ds).map(ds => {
        return {value: ds[fieldKey], label: ds.label, iconId: ds.iconId}
    })
    const conflictHandled = fieldKey in store.QuestionnaireStore.mergeOverride
    const color = showConflict && !conflictHandled ? Theme.palette.error.main : "unset"

    if (Array.isArray(valueToUse)) {
        return <MergeFieldTable table={valueToUse} fieldKey={fieldKey} iconId={iconId} />
    }

    return (
        <div style={{display: "grid", gridTemplateColumns: "16px 280px auto", padding: "4px 0px 4px 8px", gap: "15px", borderBottom: "0.5px solid #c5c5c5"}}>
            <CheckIcon index={iconId} />

            <div>{formatFieldKey(fieldKey)}</div>
            
            <div style={{display: "flex", flexWrap: "wrap", overflowWrap: "anywhere", alignItems: "center", gap: "10px", color: color}}>
                <MergeFieldValue fieldKey={fieldKey} value={valueToUse} />

                {showConflict && releventDataSources?.length > 1 ? (
                    <div style={{display: "flex", gap: "5px", marginLeft: "auto"}}>
                        <InlineMenu opener={(
                            <Button className="btn btn-xs" color={conflictHandled ? "success" : "error"} variant="contained">View conflict</Button>
                        )}>
                            <MergeConflict valueSources={releventDataSources} fieldKey={fieldKey} valueToUse={valueToUse} />
                        </InlineMenu>

                        {!conflictHandled ? (
                            <Button 
                                className="btn btn-xs" 
                                color="success" 
                                variant="contained" 
                                onClick={() => {
                                    store.QuestionnaireStore.setMergeOverride({
                                        ...store.QuestionnaireStore.mergeOverride,
                                        [fieldKey]: valueToUse
                                    })
                                }}>
                                Accept
                            </Button>
                        ) : null}
                    </div>
                ) : null}
            </div>
        </div>
    )
})


function MergeFieldValue ({fieldKey, value}) {

    if (!updatableFieldNames.includes(fieldKey)) {
        return <span>{formatValue(fieldKey, value)}</span>
    }

    return (
        <input 
            name={fieldKey} 
            value={value} 
            style={{border: "none", backgroundColor: "white", boxShadow: "0 0 10px 0 #d3d3d3", padding: "2px 4px", cursor: "text"}} 
            onChange={e => {
                store.QuestionnaireStore.setMergeOverride({...store.QuestionnaireStore.mergeOverride, [fieldKey]: e.target.value})
            }} />
    )
}


function MergeFieldTable ({table, fieldKey, iconId}) {
    return table.map((tableRow, i) => {
        if (typeof tableRow === "object" && "iconId" in tableRow) {
            iconId = tableRow.iconId
        }
        return (
            <>
                <div style={{backgroundColor: "#d7d7d7", padding: "5px"}}>
                    <small>{formatFieldKey(fieldKey)} ({i + 1})</small>
                </div>

                <div key={i} style={{borderLeft: "10px solid #d7d7d7", borderBottom: "5px solid #d7d7d7"}}>
                    {Object.keys(tableRow).filter(fieldKey => !keysToIgnore.includes(fieldKey)).map((fieldKey, i) => (
                        <MergeField key={i} dataSources={[{...tableRow, iconId: iconId}]} fieldKey={fieldKey} />
                    ))}
                </div> 
            </>
        )
    })
}

export default MergeField;